import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type {
  AccessRequestDto,
  ActivityInput,
  AdvancementInput,
  AllSettingsView,
  AdmissionStatusUsage,
  ApplyingGradeUsage,
  ApproveAccessRequestInput,
  ChangeReferralStatusInput,
  ChatPromptVersionSummary,
  AssignmentInput,
  AuditLogDto,
  AuthConfigDto,
  ConnectionTestResult,
  CheckReferralDuplicatesInput,
  CheckReferralDuplicatesResult,
  CreateCycleInput,
  CreateProspectInput,
  CycleDto,
  DashboardDto,
  DenyAccessRequestInput,
  DuplicateDto,
  EmailPreviewResponse,
  FeedbackInput,
  HouseholdDetail,
  HouseholdListResponse,
  HouseholdSummary,
  IngestionRunDetailDto,
  IngestionRunDto,
  InviteUserInput,
  MeDto,
  MyRecommendationHistory,
  DeliveryHealth,
  NotificationDto,
  NotificationListQuery,
  NotificationListResponse,
  NotificationSettingsView,
  PurgePendingInput,
  ReferralDto,
  ReferralFilterOptions,
  ReferralFormDto,
  ReferralListQuery,
  ReferralListResponse,
  ReferralReports,
  ReferralReviewDetail,
  ReferralSummary,
  SubmitReferralResult,
  EditReferralInput,
  EditReferralResult,
  TrusteeReferralActivity,
  PurgePendingResult,
  RecommendationAllotment,
  RecommendationHistory,
  PromptVersionDto,
  RecommendationImportResult,
  RecommendationInput,
  ReferralSettings,
  RenameApplyingGradeInput,
  RenameApplyingGradeResult,
  RescoreResult,
  TestEmailResult,
  ThresholdVersionDto,
  UpdateAssistantSettings,
  UpdateCycleReferralsInput,
  UpdateNotificationSettings,
  UpdateReferralSettings,
  UpdateScoringSettings,
  UpdateSftpSettings,
  UpdateUserInput,
  UpdateVeracrossSettings,
  UserDto,
  ReferralExportField,
  ReferralExportReport,
  ReferralListFilters,
} from '@hv/shared';
import { del, download, get, patch, post, put, qs } from './client';

export const keys = {
  me: ['me'] as const,
  authConfig: ['auth-config'] as const,
  households: (params: object) => ['households', params] as const,
  household: (id: string) => ['household', id] as const,
  dashboard: (cycleId?: string) => ['dashboard', cycleId ?? null] as const,
  advancement: (cycleId?: string) => ['advancement', cycleId ?? null] as const,
  cycles: ['cycles'] as const,
  users: ['users'] as const,
  settings: ['settings'] as const,
  referralSettings: ['referral-settings'] as const,
  gradeUsage: ['referral-settings', 'grade-usage'] as const,
  admissionStatuses: ['referral-settings', 'admission-statuses'] as const,
  prompts: ['prompt-versions'] as const,
  thresholds: ['threshold-versions'] as const,
  runs: ['ingestion-runs'] as const,
  run: (id: string) => ['ingestion-run', id] as const,
  duplicates: (cycleId?: string) => ['duplicates', cycleId ?? null] as const,
  audit: (params: object) => ['audit', params] as const,
  notifications: ['notifications'] as const,
  notificationList: (params: object) => ['notifications', 'list', params] as const,
  deliveryHealth: ['notifications', 'health'] as const,
  notificationSettings: ['notification-settings'] as const,
  emailPreview: ['email-preview'] as const,
  referralForm: ['referral-form'] as const,
  myReferrals: ['my-referrals'] as const,
  myReferral: (id: string) => ['my-referrals', id] as const,
  myReferralActivity: (id: string) => ['my-referrals', id, 'activity'] as const,
  myAllotment: ['my-allotment'] as const,
  myRecommendationHistory: ['my-recommendation-history'] as const,
  recommendationHistory: ['recommendation-history'] as const,
  accessRequests: ['access-requests'] as const,
  referrals: ['referrals'] as const,
  referralList: (params: object) => ['referrals', 'list', params] as const,
  referralDetail: (id: string) => ['referrals', 'detail', id] as const,
  referralFilterOptions: ['referrals', 'filter-options'] as const,
  referralReports: (cycleId?: string) => ['referrals', 'reports', cycleId ?? 'all'] as const,
};

const HOUSEHOLD_SCOPED: QueryKey[] = [['households'], ['household'], ['dashboard'], ['advancement'], ['duplicates'], ['assistant', 'chat']];

export function useInvalidateHouseholds() {
  const qc = useQueryClient();
  return () => Promise.all(HOUSEHOLD_SCOPED.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

export const useAuthConfig = () => useQuery({ queryKey: keys.authConfig, queryFn: () => get<AuthConfigDto>('/auth/config') });
export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => get<MeDto>('/auth/me'), retry: false, staleTime: 5 * 60_000 });

export interface HouseholdListParams {
  cycleId?: string;
  list: string;
  decisions?: string[];
  band: string;
  q?: string;
}

export const useHouseholds = (p: HouseholdListParams) =>
  useQuery({
    queryKey: keys.households(p),
    queryFn: () => get<HouseholdListResponse>(`/households${qs({ cycleId: p.cycleId, list: p.list, decisions: p.decisions, band: p.band, q: p.q })}`),
    placeholderData: (prev) => prev,
  });

export const useHousehold = (id: string | null) =>
  useQuery({ queryKey: keys.household(id ?? ''), queryFn: () => get<HouseholdDetail>(`/households/${id}`), enabled: !!id });

export const useDashboard = (cycleId?: string) =>
  useQuery({ queryKey: keys.dashboard(cycleId), queryFn: () => get<DashboardDto>(`/dashboard${qs({ cycleId })}`) });

export const useAdvancement = (cycleId?: string) =>
  useQuery({ queryKey: keys.advancement(cycleId), queryFn: () => get<HouseholdSummary[]>(`/advancement${qs({ cycleId })}`) });

export const useCycles = () => useQuery({ queryKey: keys.cycles, queryFn: () => get<CycleDto[]>('/cycles') });
export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => get<UserDto[]>('/users') });
export const useSettings = (enabled = true) =>
  useQuery({ queryKey: keys.settings, queryFn: () => get<AllSettingsView>('/settings'), enabled });
export const useReferralSettings = () =>
  useQuery({ queryKey: keys.referralSettings, queryFn: () => get<ReferralSettings>('/settings/referrals') });
export const useApplyingGradeUsage = () =>
  useQuery({ queryKey: keys.gradeUsage, queryFn: () => get<ApplyingGradeUsage>('/settings/referrals/grade-usage') });
export const useAdmissionStatusUsage = () =>
  useQuery({ queryKey: keys.admissionStatuses, queryFn: () => get<AdmissionStatusUsage>('/settings/referrals/admission-statuses') });
export const useChatPromptVersions = () =>
  useQuery({ queryKey: ['assistant', 'prompt-versions'], queryFn: () => get<ChatPromptVersionSummary[]>('/settings/assistant/prompt-versions') });
export const usePromptVersions = () => useQuery({ queryKey: keys.prompts, queryFn: () => get<PromptVersionDto[]>('/settings/prompt-versions') });
export const useThresholdVersions = () =>
  useQuery({ queryKey: keys.thresholds, queryFn: () => get<ThresholdVersionDto[]>('/settings/threshold-versions') });
export const useIngestionRuns = () => useQuery({ queryKey: keys.runs, queryFn: () => get<IngestionRunDto[]>('/ingestion-runs') });
export const useIngestionRun = (id: string | null) =>
  useQuery({ queryKey: keys.run(id ?? ''), queryFn: () => get<IngestionRunDetailDto>(`/ingestion-runs/${id}`), enabled: !!id });
export const useDuplicates = (cycleId?: string) =>
  useQuery({ queryKey: keys.duplicates(cycleId), queryFn: () => get<DuplicateDto[]>(`/duplicates${qs({ cycleId })}`) });

export type NotificationListParams = Partial<NotificationListQuery>;
export const fetchNotifications = (p: NotificationListParams) => get<NotificationListResponse>(`/notifications${qs({ ...p })}`);
export const useNotifications = (p: NotificationListParams) =>
  useQuery({ queryKey: keys.notificationList(p), queryFn: () => fetchNotifications(p), placeholderData: (prev) => prev, refetchInterval: 30_000 });
export const useDeliveryHealth = () => useQuery({ queryKey: keys.deliveryHealth, queryFn: () => get<DeliveryHealth>('/notifications/health'), refetchInterval: 60_000 });
export function useDeliveryActions() {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: keys.notifications });
  return {
    resend: useMutation({ mutationFn: (id: string) => post<NotificationDto>(`/notifications/${id}/resend`), onSuccess }),
    purge: useMutation({ mutationFn: (v: PurgePendingInput) => post<PurgePendingResult>('/notifications/purge-pending', v), onSuccess }),
  };
}
export const useNotificationSettings = () =>
  useQuery({ queryKey: keys.notificationSettings, queryFn: () => get<NotificationSettingsView>('/settings/notifications') });
export const useSaveNotificationSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: UpdateNotificationSettings) => put<NotificationSettingsView>('/settings/notifications', v),
    onSuccess: (data) => {
      qc.setQueryData(keys.notificationSettings, data);
      return Promise.all([qc.invalidateQueries({ queryKey: keys.emailPreview }), qc.invalidateQueries({ queryKey: keys.deliveryHealth })]);
    },
  });
};
export const useSendTestEmail = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<TestEmailResult>('/settings/notifications/test'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
  });
};
export const useEmailPreview = () => useQuery({ queryKey: keys.emailPreview, queryFn: () => get<EmailPreviewResponse>('/notifications/preview') });

export type ReferralListParams = Partial<ReferralListQuery>;
export const useReferralList = (p: ReferralListParams, enabled = true) =>
  useQuery({ queryKey: keys.referralList(p), queryFn: () => get<ReferralListResponse>(`/referrals${qs({ ...p })}`), placeholderData: (prev) => prev, enabled });
export const useReferralDetail = (id: string) =>
  useQuery({ queryKey: keys.referralDetail(id), queryFn: () => get<ReferralReviewDetail>(`/referrals/${id}`) });
export const useReferralFilterOptions = () =>
  useQuery({ queryKey: keys.referralFilterOptions, queryFn: () => get<ReferralFilterOptions>('/referrals/filter-options') });
export const referralExportQuery = (filters: ReferralListFilters, fields: readonly ReferralExportField[]) => qs({ ...filters, fields: [...fields] });
export const downloadReferralCsv = (filters: ReferralListFilters, fields: readonly ReferralExportField[]) =>
  download(`/referrals/export.csv${referralExportQuery(filters, fields)}`, 'trustee-referrals.csv');
export const useReferralExportReport = (filters: ReferralListFilters, fields: readonly ReferralExportField[]) =>
  useQuery({
    queryKey: ['referral-export', filters, fields],
    queryFn: () => get<ReferralExportReport>(`/referrals/export${referralExportQuery(filters, fields)}`),
    staleTime: Infinity,
    retry: false,
  });
export const useReferralReports = (cycleId: string | undefined, enabled = true) =>
  useQuery({ queryKey: keys.referralReports(cycleId), queryFn: () => get<ReferralReports>(`/referrals/reports${qs({ cycleId })}`), enabled });
export function useChangeReferralStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string } & ChangeReferralStatusInput) =>
      post<ReferralReviewDetail>(`/referrals/${v.id}/status`, { fromStatus: v.fromStatus, status: v.status, reason: v.reason }),
    onSettled: () => Promise.all([keys.referrals, keys.recommendationHistory, keys.notifications, ['dashboard']].map((queryKey) => qc.invalidateQueries({ queryKey }))),
  });
}

export const useAccessRequests = () =>
  useQuery({ queryKey: keys.accessRequests, queryFn: () => get<AccessRequestDto[]>('/access-requests'), refetchInterval: 60_000 });

export function useAccessRequestMutations() {
  const qc = useQueryClient();
  const onSuccess = () =>
    Promise.all([keys.accessRequests, keys.users, keys.notifications].map((queryKey) => qc.invalidateQueries({ queryKey })));
  return {
    approve: useMutation({
      mutationFn: (v: { id: string } & ApproveAccessRequestInput) => post<AccessRequestDto>(`/access-requests/${v.id}/approve`, { roles: v.roles }),
      onSuccess,
    }),
    deny: useMutation({
      mutationFn: (v: { id: string } & DenyAccessRequestInput) => post<AccessRequestDto>(`/access-requests/${v.id}/deny`, { reason: v.reason }),
      onSuccess,
    }),
  };
}

export const fetchAudit = (p: { action?: string; before?: number; limit?: number }) =>
  get<AuditLogDto[]>(`/audit-logs${qs({ action: p.action, before: p.before, limit: p.limit ?? 100 })}`);

function useHouseholdMutation<V, R>(fn: (v: V) => Promise<R>) {
  const invalidate = useInvalidateHouseholds();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidate() });
}

export const useCreateProspect = () => useHouseholdMutation((v: CreateProspectInput) => post<{ id: string }>('/households', v));
export const useUpdateApplicant = () =>
  useHouseholdMutation((v: { id: string; hvDecision?: string; enrollmentLikelihood?: string | null }) => {
    const { id, ...body } = v;
    return patch(`/applicants/${id}`, body);
  });
export const useSetOverride = () =>
  useHouseholdMutation((v: { id: string; score: number; reason: string }) => put(`/households/${v.id}/override`, { score: v.score, reason: v.reason }));
export const useClearOverride = () =>
  useHouseholdMutation((v: { id: string; reason?: string }) => del(`/households/${v.id}/override`, v.reason ? { reason: v.reason } : {}));
export const useFeedback = () =>
  useHouseholdMutation((v: { assessmentId: string } & FeedbackInput) =>
    post(`/assessments/${v.assessmentId}/feedback`, { verdict: v.verdict, comment: v.comment }),
  );
export const useRescore = () =>
  useHouseholdMutation((id: string) => post<{ status: string; score?: number; reason?: string }>(`/households/${id}/rescore`));
export const useAddActivity = () =>
  useHouseholdMutation((v: { id: string } & ActivityInput) => post(`/households/${v.id}/activities`, { type: v.type, text: v.text }));
export const useSetAssignment = () =>
  useHouseholdMutation((v: { id: string } & AssignmentInput) =>
    put(`/households/${v.id}/assignment`, { ownerUserId: v.ownerUserId, nextFollowUpAt: v.nextFollowUpAt }),
  );
export const useSetAdvancement = () =>
  useHouseholdMutation((v: { id: string } & AdvancementInput) => {
    const { id, ...body } = v;
    return put(`/households/${id}/advancement`, body);
  });
export const useAddRecommendation = () =>
  useHouseholdMutation((v: { id: string; input: RecommendationInput }) => post(`/households/${v.id}/recommendations`, v.input));
export const useResolveDuplicate = () =>
  useHouseholdMutation((v: { id: string; action: 'merge' | 'dismiss' }) => post(`/duplicates/${v.id}/${v.action}`));
export const useImportRecommendations = () =>
  useHouseholdMutation((v: { format: string; headerless: boolean; file: File }) => {
    const fd = new FormData();
    fd.append('file', v.file);
    return post<RecommendationImportResult>(`/recommendations/import${qs({ format: v.format, headerless: v.headerless })}`, fd);
  });

function useSettingsMutation<V, R = unknown>(fn: (v: V) => Promise<R>, extra: QueryKey[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([keys.settings, ...extra].map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });
}

export const useSaveVeracross = () => useSettingsMutation((v: UpdateVeracrossSettings) => put<AllSettingsView>('/settings/veracross', v));
export const useSaveSftp = () => useSettingsMutation((v: UpdateSftpSettings) => put<AllSettingsView>('/settings/sftp', v));
export const useSaveScoring = () => useSettingsMutation((v: UpdateScoringSettings) => put<AllSettingsView>('/settings/scoring', v));
export const useSaveAssistant = () => useSettingsMutation((v: UpdateAssistantSettings) => put<AllSettingsView>('/settings/assistant', v), [['assistant']]);
export const useTestVeracross = () => useMutation({ mutationFn: (v: UpdateVeracrossSettings) => post<ConnectionTestResult>('/settings/veracross/test', v) });
export const useTestSftp = () => useMutation({ mutationFn: (v: UpdateSftpSettings) => post<ConnectionTestResult>('/settings/sftp/test', v) });
export const useCreatePrompt = () =>
  useSettingsMutation((v: { template: string; notes?: string | null; activate: boolean }) => post<PromptVersionDto>('/settings/prompt-versions', v), [keys.prompts]);
export const useActivatePrompt = () =>
  useSettingsMutation((version: string) => post(`/settings/prompt-versions/${encodeURIComponent(version)}/activate`), [keys.prompts]);
export const useCreateThreshold = () =>
  useSettingsMutation(
    (v: { thresholds: { high: number; medium: number }; activate: boolean }) => post<ThresholdVersionDto>('/settings/threshold-versions', v),
    [keys.thresholds, ['households'], ['dashboard'], ['advancement']],
  );
export const useActivateThreshold = () =>
  useSettingsMutation((version: string) => post(`/settings/threshold-versions/${encodeURIComponent(version)}/activate`), [
    keys.thresholds,
    ['households'],
    ['dashboard'],
    ['advancement'],
  ]);
export const useRescoreAll = () =>
  useSettingsMutation((scope: 'stale' | 'all' | 'failed') => post<RescoreResult>('/scoring/rescore', { scope }), [['households'], ['dashboard']]);
export const useCreateCycle = () => useSettingsMutation((v: CreateCycleInput) => post<CycleDto>('/cycles', v), [keys.cycles]);
export const useSaveReferralSettings = () =>
  useSettingsMutation((v: UpdateReferralSettings) => put<ReferralSettings>('/settings/referrals', v), [keys.referralSettings]);
export const useRenameApplyingGrade = () =>
  useSettingsMutation((v: RenameApplyingGradeInput) => post<RenameApplyingGradeResult>('/settings/referrals/grades/rename', v), [keys.referralSettings, ...HOUSEHOLD_SCOPED]);
export const useUpdateCycleReferrals = () =>
  useSettingsMutation((v: { id: string } & UpdateCycleReferralsInput) => {
    const { id, ...body } = v;
    return patch<CycleDto>(`/cycles/${id}`, body);
  }, [keys.cycles]);
export const useActivateCycle = () =>
  useSettingsMutation((id: string) => post(`/cycles/${id}/activate`), [keys.cycles, ['households'], ['dashboard'], ['advancement']]);

export function useUserMutations() {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: keys.users });
  return {
    invite: useMutation({
      mutationFn: (v: InviteUserInput) => post<UserDto>('/users', v),
      onSuccess: () => Promise.all([onSuccess(), qc.invalidateQueries({ queryKey: keys.notifications })]),
    }),
    update: useMutation({
      mutationFn: (v: { id: string } & UpdateUserInput) => {
        const { id, ...body } = v;
        return patch<UserDto>(`/users/${id}`, body);
      },
      onSuccess: () => Promise.all([onSuccess(), qc.invalidateQueries({ queryKey: keys.me })]),
    }),
  };
}

export const useLogout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      window.location.assign('/login');
    },
  });
};

export const useReferralForm = () => useQuery({ queryKey: keys.referralForm, queryFn: () => get<ReferralFormDto>('/referral-form'), staleTime: 0 });
export const useMyReferrals = () => useQuery({ queryKey: keys.myReferrals, queryFn: () => get<ReferralSummary[]>('/me/referrals') });
export const useMyAllotment = () =>
  useQuery({ queryKey: keys.myAllotment, queryFn: () => get<RecommendationAllotment>('/me/allotment') });
export const useMyRecommendationHistory = () =>
  useQuery({ queryKey: keys.myRecommendationHistory, queryFn: () => get<MyRecommendationHistory>('/me/recommendation-history'), staleTime: 0 });
export const useRecommendationHistory = () =>
  useQuery({ queryKey: keys.recommendationHistory, queryFn: () => get<RecommendationHistory>('/referrals/history'), staleTime: 0 });
export const useMyReferral = (id: string | null) =>
  useQuery({ queryKey: keys.myReferral(id ?? ''), queryFn: () => get<ReferralDto>(`/me/referrals/${id}`), enabled: !!id, staleTime: 0 });
export const useDeleteReferral = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<{ ok: true }>(`/me/referrals/${id}`),
    onSuccess: (_r, id) => {
      qc.removeQueries({ queryKey: keys.myReferral(id) });
      void qc.invalidateQueries({ queryKey: keys.myAllotment });
      return qc.invalidateQueries({ queryKey: keys.myReferrals, exact: true });
    },
  });
};

export const useSubmitReferral = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; version: number; acknowledged: boolean }) => post<SubmitReferralResult>(`/me/referrals/${id}/submit`, body),
    onSuccess: (res) => {
      qc.setQueryData(keys.myReferral(res.referral.id), res.referral);
      return qc.invalidateQueries({ queryKey: keys.myReferrals, exact: true });
    },
  });
};

export const useCheckReferralDuplicates = () =>
  useMutation({
    mutationFn: (body: CheckReferralDuplicatesInput) => post<CheckReferralDuplicatesResult>('/me/referrals/check-duplicates', body),
  });

export const useEditReferral = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & EditReferralInput) => post<EditReferralResult>(`/me/referrals/${id}/edit`, body),
    onSuccess: (res) => {
      qc.setQueryData(keys.myReferral(res.referral.id), res.referral);
      void qc.invalidateQueries({ queryKey: keys.myAllotment });
      void qc.invalidateQueries({ queryKey: keys.myReferralActivity(res.referral.id) });
      return qc.invalidateQueries({ queryKey: keys.myReferrals, exact: true });
    },
  });
};

export const useMyReferralActivity = (id: string) =>
  useQuery({
    queryKey: keys.myReferralActivity(id),
    queryFn: () => get<TrusteeReferralActivity>(`/me/referrals/${id}/activity`),
    staleTime: 0,
  });

export const useRespondToClarification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; version: number; response: string }) =>
      post<ReferralDto>(`/me/referrals/${id}/clarification-response`, body),
    onSuccess: (referral) => {
      qc.setQueryData(keys.myReferral(referral.id), referral);
      void qc.invalidateQueries({ queryKey: keys.myReferralActivity(referral.id) });
      void qc.invalidateQueries({ queryKey: keys.myRecommendationHistory });
      return qc.invalidateQueries({ queryKey: keys.myReferrals, exact: true });
    },
  });
};

export const useRequestEmailCode = () => useMutation({ mutationFn: (email: string) => post<{ ok: true }>('/auth/email/request', { email }) });

export const useVerifyEmailCode = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; code: string }) => post<MeDto>('/auth/email/verify', v),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
};

export const useDevLogin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => post<MeDto>('/auth/dev-login', { email }),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
};
